import React, { useState } from 'react';
import { Badge, Button, Nav, NavItem, NavLink } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import Avatar from '../../components/Avatar';
import { resendCommunication, fetchCommunications } from '../../slices/communications.slice';

const CATEGORY_LABELS = {
    'password-reset': 'Password Reset',
    'welcome': 'Welcome',
    'order-confirmation': 'Order Confirmation',
    'order-status': 'Order Status',
    'order-placed': 'Order Placed',
    'order-request': 'Order Request',
    'order-approval': 'Order Approval',
    'order-declined': 'Order Declined',
    'order-reverted': 'Order Reverted',
    'system-notification': 'System Notification',
    'raw': 'Raw',
};

// 'queued' is awaiting retry, not a failure — it must not read as one.
const STATUS_STYLES = {
    sent: { color: 'success', icon: 'fa-check-circle' },
    queued: { color: 'warning', icon: 'fa-clock' },
    failed: { color: 'danger', icon: 'fa-times-circle' },
};

const MetadataCard = ({ label, children }) => (
    <div className="comm-metadata-card">
        <div className="label">{label}</div>
        <div>{children}</div>
    </div>
);

const CommunicationDetailPanel = ({ onClose, filterBy }) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('html');
    const [isResending, setIsResending] = useState(false);

    const { selectedCommunication: comm, selectedStatus } = useSelector(state => state.communications);
    const { user: currentUser } = useSelector(state => state.auth);

    const canResend = currentUser && ['root', 'bac-admin'].includes(currentUser.role);

    if (!comm && selectedStatus !== 'loading') return null;

    if (selectedStatus === 'loading') {
        return (
            <div className="communication-panel bg-broken shadow-lg">
                <div className="communication-panel-header">
                    <h5 className="mb-0">Loading...</h5>
                    <button className="btn btn-link text-white p-0" onClick={onClose}>
                        <span className="fas fa-times fa-lg" />
                    </button>
                </div>
                <div className="communication-panel-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    const handleResend = async () => {
        setIsResending(true);
        const promise = dispatch(resendCommunication(comm._id)).unwrap();
        toast.promise(promise, {
            pending: 'Resending email...',
            success: 'Email resent successfully',
            error: 'Failed to resend email',
        });
        try {
            await promise;
            dispatch(fetchCommunications({ ...filterBy }));
        } catch {
            // toast already handles error display
        } finally {
            setIsResending(false);
        }
    };

    const handleCopyHtml = () => {
        navigator.clipboard.writeText(comm.htmlBody || '').then(() => {
            toast.success('HTML copied to clipboard');
        }).catch(() => {
            toast.error('Failed to copy HTML');
        });
    };

    return (
        <div className="communication-panel bg-broken shadow-lg">
            <div className="communication-panel-header">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge
                        color={(STATUS_STYLES[comm.status] || STATUS_STYLES.failed).color}
                        style={{ borderRadius: 0 }}
                    >
                        <span className={`fas ${(STATUS_STYLES[comm.status] || STATUS_STYLES.failed).icon} me-1`} />
                        {comm.status}
                    </Badge>
                    <span className="text-white fw-semibold text-truncate" style={{ maxWidth: '300px' }} title={comm.subject}>
                        {comm.subject}
                    </span>
                </div>
                <button className="btn btn-link text-white p-0" onClick={onClose}>
                    <span className="fas fa-times fa-lg" />
                </button>
            </div>

            <div className="communication-panel-body">
                {/* Timestamp banner */}
                <div className="details-banner mb-4">
                    <span className="fas fa-calendar me-2 text-primary" />
                    {new Date(comm.createdAt).toLocaleString()}
                    {comm.templateCode && (
                        <Badge color="secondary" className="ms-3" style={{ borderRadius: 0 }}>
                            {comm.templateCode}
                        </Badge>
                    )}
                    {comm.category && (
                        <Badge color="info" className="ms-2" style={{ borderRadius: 0 }}>
                            {CATEGORY_LABELS[comm.category] || comm.category}
                        </Badge>
                    )}
                </div>

                {/* Metadata grid */}
                <div className="comm-metadata-grid">
                    <MetadataCard label="From">
                        <code className="small">{comm.from}</code>
                    </MetadataCard>

                    <MetadataCard label="To">
                        <div className="small">{comm.to?.join(', ')}</div>
                    </MetadataCard>

                    {comm.sesMessageId && (
                        <MetadataCard label="SES Message ID">
                            <code className="small text-truncate d-block" title={comm.sesMessageId}>
                                {comm.sesMessageId}
                            </code>
                        </MetadataCard>
                    )}

                    {comm.sesResponse && (
                        <MetadataCard label="SES Response">
                            <span className="small">{comm.sesResponse}</span>
                        </MetadataCard>
                    )}

                    {comm.accepted?.length > 0 && (
                        <MetadataCard label="Accepted">
                            <span className="small text-success">{comm.accepted.join(', ')}</span>
                        </MetadataCard>
                    )}

                    {comm.rejected?.length > 0 && (
                        <MetadataCard label="Rejected">
                            <span className="small text-danger">{comm.rejected.join(', ')}</span>
                        </MetadataCard>
                    )}
                </div>

                {/* Retry state — a queued email is still on its way */}
                {comm.status === 'queued' && (
                    <div className="mb-4 p-3 bg-warning/10 border-start border-warning border-3 rounded-end">
                        <div className="text-white-50 small mb-1">
                            <span className="fas fa-clock me-1" /> Queued for delivery
                        </div>
                        <div className="text-warning small fw-semibold">
                            {comm.attempts > 0 ? `${comm.attempts} attempt${comm.attempts === 1 ? '' : 's'} so far` : 'Not attempted yet (server was offline)'}
                            {comm.nextAttemptAt && ` — next attempt ${new Date(comm.nextAttemptAt).toLocaleString()}`}
                        </div>
                        {comm.lastError && (
                            <div className="text-white-50 small mt-1">{comm.lastError}</div>
                        )}
                    </div>
                )}

                {/* Failure details */}
                {comm.status === 'failed' && (comm.errorMessage || comm.lastError) && (
                    <div className="mb-4 p-3 bg-danger/10 border-start border-danger border-3 rounded-end">
                        <div className="text-white-50 small mb-1">
                            <span className="fas fa-exclamation-triangle me-1" /> Error
                            {comm.attempts > 0 && ` after ${comm.attempts} attempt${comm.attempts === 1 ? '' : 's'}`}
                        </div>
                        <div className="text-danger small fw-semibold">{comm.errorMessage || comm.lastError}</div>
                    </div>
                )}

                {/* User & Organization */}
                <div className="row mb-4">
                    {comm.user && (
                        <div className="col-md-6 mb-3 mb-md-0">
                            <div className="text-white-50 small mb-2">Recipient User</div>
                            <RouterNavLink to={`/users/${comm.user._id}`} className="text-decoration-none">
                                <div className="d-flex align-items-center gap-3 p-2 bg-black/30 rounded">
                                    <Avatar
                                        imageUrl={comm.user.profilePic?.imageUrl}
                                        name={`${comm.user.firstName} ${comm.user.lastName}`}
                                        size={36}
                                    />
                                    <div>
                                        <div className="fw-bold text-white">
                                            {comm.user.firstName} {comm.user.lastName}
                                        </div>
                                        <code className="small text-white-50">{comm.user.email}</code>
                                    </div>
                                </div>
                            </RouterNavLink>
                        </div>
                    )}

                    {comm.organization && (
                        <div className="col-md-6">
                            <div className="text-white-50 small mb-2">Organization</div>
                            <RouterNavLink to={`/organizations/${comm.organization._id}`} className="text-decoration-none">
                                <div className="d-flex align-items-center gap-2 p-2 bg-black/30 rounded">
                                    <span className="fas fa-building text-primary" />
                                    <span className="text-white">{comm.organization.name}</span>
                                </div>
                            </RouterNavLink>
                        </div>
                    )}
                </div>

                {/* Triggered by & Related order */}
                {(comm.triggeredBy || comm.relatedOrder) && (
                    <div className="row mb-4">
                        {comm.triggeredBy && (
                            <div className="col-md-6 mb-3 mb-md-0">
                                <div className="text-white-50 small mb-2">Triggered By</div>
                                <div className="p-2 bg-black/30 rounded small">
                                    {comm.triggeredBy.firstName} {comm.triggeredBy.lastName}
                                    <code className="ms-2 text-white-50">@{comm.triggeredBy.username}</code>
                                </div>
                            </div>
                        )}

                        {comm.relatedOrder && (
                            <div className="col-md-6">
                                <div className="text-white-50 small mb-2">Related Order</div>
                                <RouterNavLink to={`/orders/${comm.relatedOrder._id}`} className="text-decoration-none">
                                    <div className="p-2 bg-black/30 rounded small text-primary">
                                        <span className="fas fa-clipboard-list me-2" />
                                        #{comm.relatedOrder._id}
                                    </div>
                                </RouterNavLink>
                            </div>
                        )}
                    </div>
                )}

                {/* Email body preview */}
                {(comm.htmlBody || comm.textBody) && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-envelope-open me-1" /> Email Body
                        </div>

                        <Nav tabs className="comm-body-tabs border-bottom border-secondary mb-3">
                            {comm.htmlBody && (
                                <NavItem>
                                    <NavLink
                                        className={activeTab === 'html' ? 'active' : ''}
                                        onClick={() => setActiveTab('html')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Rendered HTML
                                    </NavLink>
                                </NavItem>
                            )}
                            {comm.textBody && (
                                <NavItem>
                                    <NavLink
                                        className={activeTab === 'text' ? 'active' : ''}
                                        onClick={() => setActiveTab('text')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Plain Text
                                    </NavLink>
                                </NavItem>
                            )}
                        </Nav>

                        {activeTab === 'html' && comm.htmlBody && (
                            <iframe
                                className="comm-html-preview"
                                srcDoc={comm.htmlBody}
                                sandbox=""
                                title="Email HTML preview"
                            />
                        )}

                        {activeTab === 'text' && comm.textBody && (
                            <pre className="comm-text-preview">{comm.textBody}</pre>
                        )}
                    </div>
                )}

                {/* Action buttons */}
                <div className="d-flex gap-2 flex-wrap">
                    {canResend && (
                        <Button
                            color="primary"
                            outline
                            size="sm"
                            onClick={handleResend}
                            disabled={isResending}
                        >
                            <span className="fas fa-paper-plane me-2" />
                            Resend
                        </Button>
                    )}

                    {comm.htmlBody && (
                        <Button color="secondary" outline size="sm" onClick={handleCopyHtml}>
                            <span className="fas fa-code me-2" />
                            Copy raw HTML
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CommunicationDetailPanel;
